import { Checkbox } from 'component/Checkbox'
import { Option, toggleOption } from 'component/Select/Option'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { observer } from 'mobx-react-lite'
import { FilterStore } from 'page/catalog/store/FilterStore'
import { Fragment } from 'react'
import { getTranslation } from 'util/translations'
import { disk, emdash } from 'util/typo'

type Props = {
  filter: FilterStore
  options: Option[]
}

export const FilterList = observer((props: Props) => {
  const { filter, options } = props
  const { needTranslate, type } = filter

  const setValue = useLatestCallback((value: string[]) => {
    filter.value = value
  })

  const onChangeItem = useLatestCallback((key: string) => {
    const next = toggleOption(key, filter.value, options)
    setValue(next)
  })

  function match(option: Option) {
    const text = filter.search.trim().toLowerCase()
    if (!text) return true

    const optValue = option.value.toLowerCase()
    const optLabel = (option.label || '').toLowerCase()

    const value = needTranslate
      ? getTranslation(type, optValue).toLowerCase()
      : optValue
    const label = needTranslate
      ? getTranslation(type, optLabel).toLowerCase()
      : optLabel

    return value.includes(text) || label.includes(text)
  }

  const filtered = options.filter(match)
  let current_group: string | undefined = ''
  let current_label: string | undefined = ''
  let used_values = new Set<string>

  return (
    <Vertical gap={12} pad={[12, 8]}>
      {!filtered.length && (
        <Space height={24} center>
          <Typo size={12} secondary>
            {i18n('NothingFound')}
          </Typo>
        </Space>
      )}
      {filtered.map((option: Option) => {
        const { value, label, group } = option
        const checked = filter.value.includes(value)
        const title = group && group !== current_group
        const current = current_label === label && current_group === group
        const used = used_values.has(value)
        if (current || used) return null

        current_group = group
        current_label = label
        used_values.add(value)

        return (
          <Fragment key={value}>
            {title && (
              <Space gap={4} wrap>
                {group
                  .split('>')
                  .map((g) => g.trim())
                  .map((g, i) => (
                    <Fragment key={i}>
                      {i > 0 && (
                        <Typo size={11} accent tertiary>
                          {disk}
                        </Typo>
                      )}
                      <Typo size={15} accent secondary>
                        {g}
                      </Typo>
                    </Fragment>
                  ))}
              </Space>
            )}
            <Space gap={12}>
              <Checkbox checked={checked} onChange={() => onChangeItem(value)}>
                {label || value || emdash}
              </Checkbox>
            </Space>
          </Fragment>
        )
      })}
    </Vertical>
  )
})
